/**
 * Handles notification popups (NON-MODAL)
 * @module notificationPopup
 */

import $ from 'jquery';
import Keyboard from './keyboard';

export const DEFAULTS = {
  message: '',
  duration: 5000,
  container: document.body,
  showCloseButton: true,
  trapFocusFunc: Keyboard.trapFocus.bind(Keyboard),
  releaseFocusFunc: Keyboard.releaseFocus.bind(Keyboard),
};

/**
 * Creates a notification popup
 * @private
 * @param {string} message - The message of the notification
 * @param {boolean} showCloseButton - Whether to show the close button
 * @returns {string} Fully qualified notification
 */
const createNotification = ({ message, showCloseButton }) => {
  const closeButton = showCloseButton ? `
    <div class="u-center u-margin-t--sm">
      <button class="js-notification-popup-close link u-color--white u-text--lg" type="button">
        close
      </button>
    </div>
  ` : '';

  return `
    <div class="js-notification-popup notification-popup" role="status" aria-live="polite">
      <p class="u-margin-b--none u-center modal__focus-element">${message}</p>
      ${closeButton}
    </div>
  `;
};


/**
 * Return focus to the element that triggered the notification
 * @private
 * @param {jQuery} $notification - The notification element
 */
const returnToLastFocus = ($notification, releaseFocusFunc) => {
  const $lastFocus = $(document).data('notificationLastFocus');
  if (typeof releaseFocusFunc === 'function') {
    releaseFocusFunc($notification, $lastFocus);
  }
  $(document).removeData('notificationLastFocus');
};


/**
 * Closes and removes a notification
 * @private
 * @param {jQuery} $notification - The notification element to close
 */
const closeNotification = ($notification, releaseFocusFunc) => {
  $notification.removeClass('is-active');

  // Remove from DOM after transition
  setTimeout(() => {
    returnToLastFocus($notification, releaseFocusFunc);
    $notification.off('keydown.notificationEscape');
    $notification.remove();
  }, 300); // Match CSS transition duration
};

/**
 * Save last focus element before showing notification
 * @private
 * @param {jQuery} $triggerElement - Element that triggered the notification
 */
const saveLastFocus = ($triggerElement) => {
  if ($triggerElement && $triggerElement.length) {
    $(document).data('notificationLastFocus', $triggerElement);
  }
};

/**
 * Handle escape key to close notification
 * @private
 * @param {Event} evt - Keyboard event
 * @param {jQuery} $notification - The notification element
 * @param {Function} releaseFocusFunc - Function to release focus trap
 */
const escapeNotification = (evt, $notification, releaseFocusFunc) => {
  if (Keyboard.parseKey(evt) === Keyboard.keys.ESCAPE) {
    closeNotification($notification, releaseFocusFunc);
  }
};

/**
 * Shows a notification popup
 * @param {Object} options - Configuration options
 * @see DEFAULTS
 */
export default function notificationPopup(options = {}) {
  const opts = $.extend({}, DEFAULTS, options);

  // Close any existing notifications first
  $('.js-notification-popup.is-active').each(function closePopup() {
    closeNotification($(this));
  });

  // Save the currently focused element
  saveLastFocus($(document.activeElement));

  const $notification = $(createNotification({
    message: opts.message,
    showCloseButton: opts.showCloseButton,
  }));

  $notification.appendTo(opts.container);
  $notification.addClass('is-active');

  // Set up escape key handler
  $notification.on('keydown.notificatinEscape', (evt) => {
    escapeNotification(evt, $notification, opts.releaseFocusFunc);
  });

  // Add click handler for close button if present
  if (opts.showCloseButton) {
    $notification.find('.js-notification-popup-close').on('click', () => {
      closeNotification($notification, opts.releaseFocusFunc);
    });
  }

  // Trap focus and move focus to notification
  opts.trapFocusFunc($notification);

  // Focus on the message or close button
  const $focusTarget = opts.showCloseButton
      ? $notification.find('.js-notification-popup-close')
      : $notification.find('.modal__focus-element');

  $focusTarget.focus();

  // Auto-remove after duration
  if (opts.duration > 0) {
    setTimeout(() => {
      closeNotification($notification, opts.releaseFocusFunc);
    }, opts.duration);
  }

  return {
    close: () => closeNotification($notification, opts.releaseFocusFunc),
  };
}

/**
 * Utility function for showing notifications
 * @param {string|Object} messageOrOptions - Message string or options object
 */
$.notificationPopup = (messageOrOptions) => {
  if (typeof messageOrOptions === 'string') {
    return notificationPopup({ message: messageOrOptions });
  }
  return notificationPopup(messageOrOptions);
};

/**
 * Utility function for closing all notifications
 */
$.notificationPopup.close = () => {
  $('.js-notification-popup.is-active').each(function closePopup() {
    closeNotification($(this), DEFAULTS.releaseFocusFunc);
  });
};



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/notification-popup.js